import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from '../assets/close.svg';
import { ReactComponent as SearchIcon } from '../assets/search.svg';
import { ReactComponent as ChevronIcon } from '../assets/chevron.svg';
import {
  buildGuideIndex,
  getGuidePage,
  getGuideGroups,
  getGuidePages,
  guideBlockSection,
  guideGroupOf,
  guidePlainText,
  guideSearchHitDomId,
  guideRowWhy,
  guideSectionDomId,
  guideSnippet,
  guideStep,
  GuideBlock,
  GuideInlineNode,
  GuidePageId,
  GuideSearchHit,
  GuideVariant,
  parseGuideInline,
  searchGuide,
} from './guideContent';
import { GUIDE_ART } from './guideArt';
import styles from './GraphGuidePanel.module.scss';

type GraphGuidePanelProps = {
  variant: GuideVariant;
  page: GuidePageId;
  // section anchor to scroll to and spotlight on open (a hint's "Learn more"
  // or a search hit); a `block-N` anchor lands on the Nth block of the page
  section?: string | null;
  onPageChange: (page: GuidePageId, section?: string) => void;
  onClose: () => void;
};

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const FLASH_MS = 1100;

// The in-app Graph guide (delivery prototypes graph-delivery*.html): a right
// drawer over a dimmed canvas. Header with the title, a docs-style search whose
// results drop under it, and the close control; below, a collapsible table of
// contents on the left and one article on the right. Mounted only while open —
// the page keeps the last-read page so reopening lands where the reader left off.
export const GraphGuidePanel: React.FC<GraphGuidePanelProps> = ({
  variant,
  page,
  section,
  onPageChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchBoxRef = useRef<HTMLFormElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [resultsOpen, setResultsOpen] = useState(false);
  const [activeHit, setActiveHit] = useState(0);
  // nav groups the reader collapsed, by label
  const [closedGroups, setClosedGroups] = useState<Record<string, boolean>>({});
  // DOM id of the block spotlighted after a deep link / search landing
  const [flashId, setFlashId] = useState<string | null>(null);
  const dialogTitleId = useId();
  const pageTitleId = useId();
  const searchId = useId();
  const resultsId = useId();

  const groups = useMemo(() => getGuideGroups(variant), [variant]);
  const pages = useMemo(() => getGuidePages(variant), [variant]);
  const currentPage = getGuidePage(variant, page);
  const pageTitle = useCallback(
    (id: GuidePageId) => t(getGuidePage(variant, id).title),
    [t, variant],
  );

  // dialog housekeeping: focus in on open, return focus to the opener (the
  // header button or a tooltip's "Learn more") on close
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      opener?.focus?.();
    };
  }, []);

  // opening a page opens its nav group
  useEffect(() => {
    const label = guideGroupOf(variant, currentPage.id);
    if (label)
      setClosedGroups((closed) =>
        closed[label] ? { ...closed, [label]: false } : closed,
      );
  }, [variant, currentPage.id]);

  // deep link: scroll the target into view, move focus onto it (so a screen
  // reader starts reading at the section, not the panel title) and flash it.
  // Unknown ids leave the reader at the page top.
  const jumpToSection = useCallback(
    (pageId: GuidePageId, sectionId: string) => {
      const domId = guideSectionDomId(pageId, sectionId);
      // a block carries its index anchor and, optionally, a named one — see anchorProps
      const target = document.querySelector<HTMLElement>(
        `[data-guide-anchors~="${domId}"]`,
      );
      if (!target) return;
      // search hits land centred like the prototype; named sections align to the top
      target.scrollIntoView({
        block: sectionId.startsWith('block-') ? 'center' : 'start',
      });
      target.focus({ preventScroll: true });
      setFlashId(domId);
    },
    [],
  );
  useEffect(() => {
    if (!section) return;
    jumpToSection(currentPage.id, section);
  }, [currentPage.id, section, jumpToSection]);
  useEffect(() => {
    if (!flashId) return;
    const timer = window.setTimeout(() => setFlashId(null), FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashId]);

  // search index over the translated text, rebuilt only when the language or
  // the guide variant changes
  const searchIndex = useMemo(() => buildGuideIndex(pages, t), [pages, t]);
  const trimmedQuery = query.trim();
  const hits = useMemo(
    () => searchGuide(searchIndex, trimmedQuery),
    [searchIndex, trimmedQuery],
  );
  const showResults = resultsOpen && trimmedQuery.length > 0;

  const closeResults = useCallback(() => setResultsOpen(false), []);

  const openHit = (hit: GuideSearchHit) => {
    closeResults();
    if (hit.block < 0) {
      onPageChange(hit.page);
      articleRef.current?.scrollTo({ top: 0 });
      return;
    }
    const anchor = guideBlockSection(hit.block);
    onPageChange(hit.page, anchor);
    // same page + same anchor: props don't change, jump by hand
    if (hit.page === currentPage.id && anchor === section)
      jumpToSection(hit.page, anchor);
  };

  // Escape works through the search first: results, then the query, then the
  // guide. Captured on document with stopPropagation so the graph's own Escape
  // listeners (unpin node, close the embedding overlay) never see the keypress.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (showResults) {
        closeResults();
        return;
      }
      if (
        document.activeElement === searchRef.current &&
        searchRef.current?.value
      ) {
        setQuery('');
        return;
      }
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [onClose, showResults, closeResults]);

  // a click anywhere outside the search box and its results closes them
  useEffect(() => {
    if (!showResults) return;
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        resultsRef.current?.contains(target) ||
        searchBoxRef.current?.contains(target)
      )
        return;
      closeResults();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [showResults, closeResults]);

  // keep Tab inside the dialog: the graph behind is covered by the backdrop
  // and must not receive focus while the guide is open
  const trapTab = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;
    const focusable = Array.from(
      dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    );
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (
      !event.shiftKey &&
      (active === last || active === dialogRef.current)
    ) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (!showResults || hits.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveHit((index) => (index + 1) % hits.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveHit((index) => (index - 1 + hits.length) % hits.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      openHit(hits[activeHit]);
    }
  };

  // the active result follows the arrow keys into view
  useEffect(() => {
    if (!showResults) return;
    document
      .getElementById(guideSearchHitDomId(resultsId, activeHit))
      ?.scrollIntoView({ block: 'nearest' });
  }, [showResults, activeHit, resultsId]);

  const resultCount =
    hits.length === 0
      ? t('No results')
      : t('guideSearchResults', { count: hits.length });

  // id / focusability / flash class for a section anchor
  const anchorProps = (ids: (string | undefined)[], baseClass?: string) => {
    const named = ids.filter(Boolean) as string[];
    if (named.length === 0) return { className: baseClass };
    const domIds = named.map((id) => guideSectionDomId(currentPage.id, id));
    const flashed = flashId !== null && domIds.includes(flashId);
    return {
      // one element can carry a single id, so jumpToSection resolves anchors
      // through the data attribute, which lists the block index and the named
      // section together
      'data-guide-anchors': domIds.join(' '),
      tabIndex: -1,
      className: [baseClass, styles.anchor, flashed ? styles.anchorFlash : '']
        .filter(Boolean)
        .join(' '),
    };
  };

  const renderInline = (text: string): React.ReactNode => {
    const render = (nodes: GuideInlineNode[]): React.ReactNode =>
      nodes.map((node, index) => {
        if (typeof node === 'string') return node;
        if (node.type === 'ui')
          return (
            <b key={index} className={styles.ui}>
              {node.text}
            </b>
          );
        if (node.type === 'em')
          return (
            <strong key={index} className={styles.em}>
              {node.text}
            </strong>
          );
        return (
          <button
            key={index}
            type="button"
            className={styles.xref}
            onClick={() => onPageChange(node.page, node.section)}
          >
            {render(node.children)}
          </button>
        );
      });
    return render(parseGuideInline(t(text)));
  };

  const renderBlock = (block: GuideBlock, index: number) => {
    const blockId = guideBlockSection(index);
    const named = 'id' in block ? block.id : undefined;
    switch (block.type) {
      case 'p':
        return (
          <p key={index} {...anchorProps([blockId, named], styles.paragraph)}>
            {renderInline(block.text)}
          </p>
        );
      case 'h':
        return (
          <h3 key={index} {...anchorProps([blockId, named], styles.subheading)}>
            {renderInline(block.text)}
          </h3>
        );
      case 'note':
        return (
          <p key={index} {...anchorProps([blockId, named], styles.note)}>
            {renderInline(block.text)}
          </p>
        );
      case 'steps':
        return (
          <ol key={index} {...anchorProps([blockId], styles.steps)}>
            {block.items.map(guideStep).map((item, step) => (
              <li key={item.text} className={styles.step}>
                <span className={styles.stepNumber} aria-hidden="true">
                  {step + 1}
                </span>
                <span className={styles.stepText}>
                  {renderInline(item.text)}
                  {item.why && (
                    <span className={styles.stepWhy}>
                      {renderInline(item.why)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        );
      case 'list':
        return (
          <ul
            key={index}
            {...anchorProps([blockId], `${styles.list} ${styles.bullets}`)}
          >
            {block.items.map((item) => (
              <li key={item}>{renderInline(item)}</li>
            ))}
          </ul>
        );
      case 'rows':
        return (
          <div key={index} {...anchorProps([blockId], styles.tableWrap)}>
            <table className={styles.table}>
              <tbody>
                {block.rows.map((row) => (
                  <tr key={row.key} {...anchorProps([row.id])}>
                    <th scope="row" className={styles.tableKey}>
                      {renderInline(row.key)}
                    </th>
                    <td className={styles.tableValue}>
                      {renderInline(row.text)}
                      {row.rule && (
                        <span className={styles.tableRule}>
                          {renderInline(row.rule)}
                        </span>
                      )}
                      {guideRowWhy(row).map((why) => (
                        <span key={why} className={styles.tableWhy}>
                          {renderInline(why)}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'tiles':
        return (
          <div key={index} {...anchorProps([blockId], styles.tiles)}>
            {block.items.map((item) => (
              <button
                key={item.page}
                type="button"
                className={styles.tile}
                onClick={() => onPageChange(item.page)}
              >
                {GUIDE_ART[item.page] && (
                  <span className={styles.tileArt}>
                    <img src={GUIDE_ART[item.page]} alt="" draggable={false} />
                  </span>
                )}
                <span className={styles.tileTitle}>{t(item.title)}</span>
                <span className={styles.tileText}>
                  {renderInline(item.text)}
                </span>
              </button>
            ))}
          </div>
        );
      case 'cards':
        return (
          <div key={index} {...anchorProps([blockId], styles.cards)}>
            {block.items.map((item) => (
              <button
                key={item.page}
                type="button"
                className={styles.card}
                onClick={() => onPageChange(item.page)}
              >
                <span className={styles.cardTitle}>{t(item.title)}</span>
                <span className={styles.cardText}>
                  {renderInline(item.text)}
                </span>
              </button>
            ))}
          </div>
        );
      case 'related':
        return (
          <div key={index} {...anchorProps([blockId], styles.related)}>
            <span className={styles.relatedHeading}>{t('Learn more')}</span>
            {block.items.map((item) => (
              <button
                key={`${item.page}#${item.section ?? ''}`}
                type="button"
                className={styles.relatedLink}
                onClick={() => onPageChange(item.page, item.section)}
              >
                <span className={styles.relatedTitle}>
                  {item.title ? t(item.title) : pageTitle(item.page)}
                </span>
              </button>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  const renderHit = (hit: GuideSearchHit, index: number) => {
    const snippet = guideSnippet(hit.text, trimmedQuery);
    const active = index === activeHit;
    return (
      <div
        key={`${hit.page}-${hit.block}`}
        id={guideSearchHitDomId(resultsId, index)}
        role="option"
        aria-selected={active}
        className={`${styles.hit}${active ? ` ${styles.hitActive}` : ''}`}
        onMouseMove={() => setActiveHit(index)}
        onClick={() => openHit(hit)}
      >
        <svg
          className={styles.hitIcon}
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          aria-hidden="true"
        >
          <path d="M4 1.8h5l3 3v9.4H4z" strokeLinejoin="round" />
          <path d="M9 1.8v3h3" strokeLinejoin="round" />
        </svg>
        <span className={styles.hitText}>
          <span className={styles.hitPage}>{hit.pageTitle}</span>
          <span className={styles.hitLine}>
            {snippet.before}
            {snippet.match && (
              <mark className={styles.mark}>{snippet.match}</mark>
            )}
            {snippet.after}
          </span>
        </span>
        <ChevronIcon className={styles.hitGo} aria-hidden="true" />
      </div>
    );
  };

  return (
    <div className={styles.root}>
      {/* click-to-close scrim; keyboard users close with Escape or the X button */}
      <div className={styles.backdrop} onClick={onClose} aria-hidden="true" />
      <div
        ref={dialogRef}
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={dialogTitleId}
        tabIndex={-1}
        onKeyDown={trapTab}
      >
        <div className={styles.head}>
          <h2 id={dialogTitleId} className={styles.title}>
            {t('Graph guide')}
          </h2>
          <form
            ref={searchBoxRef}
            role="search"
            className={styles.searchBox}
            onSubmit={(event) => event.preventDefault()}
          >
            <SearchIcon className={styles.searchIcon} aria-hidden="true" />
            <label htmlFor={searchId} className={styles.srOnly}>
              {t('Search the guide')}
            </label>
            <input
              ref={searchRef}
              id={searchId}
              type="text"
              autoComplete="off"
              role="combobox"
              aria-expanded={showResults}
              aria-controls={resultsId}
              aria-autocomplete="list"
              aria-activedescendant={
                showResults && hits.length > 0
                  ? guideSearchHitDomId(resultsId, activeHit)
                  : undefined
              }
              placeholder={t('Search the guide')}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActiveHit(0);
                setResultsOpen(true);
              }}
              onFocus={() => setResultsOpen(true)}
              onKeyDown={handleSearchKeyDown}
            />
            {trimmedQuery && (
              <span className={styles.count} aria-hidden="true">
                {resultCount}
              </span>
            )}
            {query && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={() => {
                  setQuery('');
                  searchRef.current?.focus();
                }}
                aria-label={t('Clear search')}
              >
                <CloseIcon aria-hidden="true" />
              </button>
            )}
          </form>
          <span className={styles.srOnly} role="status" aria-live="polite">
            {trimmedQuery ? resultCount : ''}
          </span>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('Close guide')}
          >
            <CloseIcon aria-hidden="true" />
          </button>
          {/* results drop under the header, over the article */}
          {showResults && (
            <div ref={resultsRef} className={styles.results}>
              <div
                id={resultsId}
                role="listbox"
                aria-label={t('Search results')}
                className={styles.hitList}
              >
                {hits.length > 0 ? (
                  hits.map(renderHit)
                ) : (
                  <p className={styles.noResults}>
                    {t('Nothing matches “{{query}}”.', {
                      query: trimmedQuery,
                      interpolation: { escapeValue: false },
                    })}
                  </p>
                )}
              </div>
              <div className={styles.resultsFoot} aria-hidden="true">
                <kbd className={styles.key}>↑</kbd>
                <kbd className={styles.key}>↓</kbd> {t('Navigate')}{' '}
                <kbd className={styles.key}>Enter</kbd> {t('Open')}{' '}
                <kbd className={styles.key}>Esc</kbd> {t('Close')}
              </div>
            </div>
          )}
        </div>
        <div className={styles.body}>
          <nav className={styles.nav} aria-label={t('Guide pages')}>
            {groups.map((group) => {
              const plain = !group.label;
              const open = plain || !closedGroups[group.label];
              const groupId = `${searchId}-group-${group.label.replace(
                /\W+/g,
                '-',
              )}`;
              return (
                <div
                  key={group.label}
                  className={`${styles.group}${
                    plain ? ` ${styles.groupPlain}` : ''
                  }`}
                >
                  {!plain && (
                    <button
                      type="button"
                      id={groupId}
                      className={styles.groupTitle}
                      aria-expanded={open}
                      onClick={() =>
                        setClosedGroups((closed) => ({
                          ...closed,
                          [group.label]: open,
                        }))
                      }
                    >
                      <span>{t(group.label)}</span>
                      <ChevronIcon
                        className={`${styles.chevron}${
                          open ? ` ${styles.chevronOpen}` : ''
                        }`}
                        aria-hidden="true"
                      />
                    </button>
                  )}
                  {open && (
                    <ul
                      className={styles.pageList}
                      aria-labelledby={plain ? undefined : groupId}
                    >
                      {group.pages.map((entry) => {
                        const active = entry.id === currentPage.id;
                        return (
                          <li key={entry.id}>
                            <button
                              type="button"
                              className={`${styles.navItem}${
                                active ? ` ${styles.navItemActive}` : ''
                              }`}
                              aria-current={active ? 'page' : undefined}
                              onClick={() => {
                                onPageChange(entry.id);
                                articleRef.current?.scrollTo({ top: 0 });
                              }}
                            >
                              {t(entry.navTitle ?? entry.title)}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </nav>
          {/* keyed by page so a page switch remounts the scroll container at the top */}
          <article
            ref={articleRef}
            key={currentPage.id}
            className={styles.article}
            aria-labelledby={pageTitleId}
          >
            <h2 id={pageTitleId} className={styles.pageTitle}>
              {guidePlainText(t(currentPage.title))}
            </h2>
            {currentPage.blocks.map(renderBlock)}
          </article>
        </div>
      </div>
    </div>
  );
};
